import { Trash2, AlertTriangle } from "lucide-react";
import type { Node, Edge } from "@xyflow/react";
import { TriggerConfig } from "./config/trigger-config";
import { AgentConfig } from "./config/agent-config";
import { ConditionConfig } from "./config/condition-config";
import { ActionConfig } from "./config/action-config";

/** One selectable prompt-template variable. */
export interface PromptVariable {
  value: string;
  label: string;
  hint?: string;
}

/**
 * Variables available to a node's prompt template: the workflow input, plus
 * upstream node outputs. When node JSON declares an outputSchema, show exact
 * variables; otherwise keep the editable scaffold.
 */
function upstreamVariables(nodeId: string, nodes: Node[], edges: Edge[]): PromptVariable[] {
  const sourcesByTarget = new Map<string, string[]>();
  for (const e of edges) {
    const list = sourcesByTarget.get(e.target) ?? [];
    list.push(e.source);
    sourcesByTarget.set(e.target, list);
  }
  const upstream = new Set<string>();
  const queue = [...(sourcesByTarget.get(nodeId) ?? [])];
  while (queue.length) {
    const id = queue.shift()!;
    if (upstream.has(id)) continue;
    upstream.add(id);
    for (const s of sourcesByTarget.get(id) ?? []) queue.push(s);
  }
  const vars: PromptVariable[] = [
    { value: "{{input}}", label: "input", hint: "Workflow trigger input" },
  ];
  for (const n of nodes) {
    if (!upstream.has(n.id)) continue;
    const label = nodeLabel(n);
    const outputKeys = outputKeysForNode(n);
    if (outputKeys.length > 0) {
      for (const key of outputKeys) {
        vars.push({
          value: `{{steps.${n.id}.outputs.${key}}}`,
          label: `${label}.${key}`,
          hint: `From ${label}`,
        });
      }
      continue;
    }
    vars.push({
      value: `{{steps.${n.id}.outputs.}}`,
      label,
      hint: `steps.${n.id}.outputs.<key>`,
    });
  }
  return vars;
}

function nodeLabel(node: Node): string {
  const data = (node.data ?? {}) as Record<string, unknown>;
  if (typeof data.label === "string" && data.label.trim()) return data.label.trim();
  if (typeof node.type === "string" && node.type.trim()) return node.type;
  return node.id;
}

/**
 * Same slug rule the canvas uses to turn a title into a node id. Two labels
 * collide as ids exactly when their slugs match, so we compare on the slug.
 */
function labelSlug(label: unknown): string {
  return (typeof label === "string" ? label : "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function outputKeysForNode(node: Node): string[] {
  const data = (node.data ?? {}) as Record<string, unknown>;
  const schema = data.outputSchema;
  if (!schema || typeof schema !== "object") return [];
  const properties = (schema as { properties?: unknown }).properties;
  if (!properties || typeof properties !== "object" || Array.isArray(properties)) {
    return [];
  }
  return Object.keys(properties).sort();
}

/**
 * Right-rail config panel. Dispatches to the right per-type form based on
 * the selected node's `type`. When no node is selected, shows a placeholder.
 *
 * Admins edit, viewers see the same form in read-only mode.
 */
export function ConfigPanel({
  node,
  nodes,
  edges,
  onChange,
  onDelete,
  readOnly,
  workflowId,
}: {
  node: Node | null;
  nodes: Node[];
  edges: Edge[];
  onChange: (data: Record<string, unknown>) => void;
  onDelete: () => void;
  readOnly: boolean;
  workflowId: string;
}) {
  if (!node) {
    return (
      <div className="flex w-[320px] shrink-0 items-center justify-center border-l border-border bg-surface p-6 text-center">
        <p className="text-xs text-text-muted">
          Select a node to configure it, or drag a new one from the left rail.
        </p>
      </div>
    );
  }

  const data = (node.data ?? {}) as Record<string, unknown>;
  const variables = upstreamVariables(node.id, nodes, edges);

  // Warn when this node's title matches another node's, because the id is
  // derived from the title: a duplicate title forces a "-2" id that no longer
  // matches the label. Nudging the user to a unique name keeps title and id in
  // sync. Compare on the slug so "Score Lead" and "score-lead" count as a clash.
  const currentLabel =
    typeof data.label === "string" ? data.label.trim() : "";
  const currentSlug = labelSlug(currentLabel);
  const duplicateName =
    !!currentSlug &&
    nodes.some(
      (n) =>
        n.id !== node.id &&
        labelSlug((n.data as { label?: unknown } | undefined)?.label) ===
          currentSlug,
    );

  return (
    <div
      data-tour="config-panel"
      className="flex w-[320px] shrink-0 flex-col border-l border-border bg-surface"
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {node.type} node
        </p>
        {!readOnly && (
          <button
            onClick={onDelete}
            aria-label="Delete node"
            className="flex h-7 w-7 items-center justify-center rounded text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {!readOnly && duplicateName && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-2 text-xs text-warning">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>
              Another node is already named &quot;{currentLabel}&quot;. Names must
              be unique so each step keeps its own id, otherwise this one is saved
              as a different id than its label. Rename it to something unique.
            </span>
          </div>
        )}
        {node.type === "trigger" && (
          <TriggerConfig
            data={data}
            onChange={onChange}
            readOnly={readOnly}
            workflowId={workflowId}
          />
        )}
        {node.type === "agent" && (
          <AgentConfig
            data={data}
            onChange={onChange}
            readOnly={readOnly}
            variables={variables}
          />
        )}
        {node.type === "condition" && (
          <ConditionConfig
            data={data}
            onChange={onChange}
            readOnly={readOnly}
            variables={variables}
          />
        )}
        {node.type === "action" && (
          <ActionConfig
            data={data}
            onChange={onChange}
            readOnly={readOnly}
            variables={variables}
          />
        )}
      </div>
      {readOnly && (
        <div className="border-t border-border bg-background/50 px-4 py-2">
          <p className="text-[11px] text-text-muted">
            Only tenant admins can edit workflows.
          </p>
        </div>
      )}
    </div>
  );
}
